export type GridLocation = {
  rowIndex: number
  row: number
  isEven: boolean
}

export type TilePositions = {
  even: number[]
  odd: number[]
}
const getBlurRange = (minScale: number, maxScale: number, bluePercentFromSides: number) => {
  const rangeFromEdges = (maxScale - minScale) * bluePercentFromSides
  return [minScale + rangeFromEdges, maxScale - rangeFromEdges]
}

const durstenfeldArrayShuffle = (array: number[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = array[i]
    array[i] = array[j]
    array[j] = temp
  }

  return array
}

const getNumberOfTilesPerHalfHorizontally = (
  factor: number,
  tileWidth: number,
  upperBoundScaleRange: number,
  cloudTextAreaWidth: number,
  forSides = false
) => {
  if (forSides) {
    return (window.document.body.clientWidth - cloudTextAreaWidth) / ((tileWidth * upperBoundScaleRange * factor) / 2)
  }

  const aspectRatioTileFactorAdjustment = window.document.body.clientWidth / window.innerHeight

  return window.document.body.clientWidth / ((tileWidth * factor * aspectRatioTileFactorAdjustment) / 2)
}

const getRandomNumber = (min: number, max: number) => {
  return Math.floor(Math.random() * (max * 10 - min * 10 + 1) + min * 10) / 10
}

const getRandomConstrainedYCoord = (
  min: number,
  max: number,
  randomScaleValue: number,
  isTop: boolean,
  cloudTextAreaRefHeight: number,
  tileDimensionsHeight: number,
  mobilePadding: number,
  alternateNewTilePosTrackerBottom = false,
  alternateNewTilePosTrackerTop = false,
  isMobile = false
): number => {
  const quarterOfAvailableSpace = (window.innerHeight - cloudTextAreaRefHeight) / 4
  const transformedHeight = randomScaleValue * tileDimensionsHeight
  if (isTop) {
    if (alternateNewTilePosTrackerTop) {
      max = min + quarterOfAvailableSpace
    } else {
      max = min + (window.innerHeight - cloudTextAreaRefHeight - transformedHeight) / 2

      min = min + quarterOfAvailableSpace
    }
    if (isMobile) {
      max -= mobilePadding
      min -= mobilePadding
    }
  } else {
    if (alternateNewTilePosTrackerBottom) {
      min = min + (window.innerHeight + cloudTextAreaRefHeight + transformedHeight) / 2
      max = min + quarterOfAvailableSpace - transformedHeight / 2
    } else {
      min = max - quarterOfAvailableSpace
    }

    if (isMobile) {
      max += mobilePadding
      min += mobilePadding
    }
  }

  return getRandomNumber(min, max)
}

const getRandomConstrainedXCoord = (
  horizontalInterval: number,
  randomScaleValue: number,
  tileMin: number,
  isTop: boolean,
  counter: number,
  tileWidth: number,
  cloudTextAreaWidth: number,
  tileDimensionsWidth: number,
  side = 'none'
): number => {
  let max: number, min: number

  if (side == 'left') {
    max =
      tileMin +
      horizontalInterval +
      horizontalInterval * counter -
      Math.abs(tileWidth * randomScaleValue - 1.5 * tileWidth)
    min = tileMin + horizontalInterval * counter - Math.abs(tileWidth * randomScaleValue - tileWidth)
    return max
  } else if (side == 'right') {
    max =
      tileMin +
      window.document.body.clientWidth / 2 +
      cloudTextAreaWidth / 2 +
      (tileDimensionsWidth * randomScaleValue) / 2 +
      horizontalInterval * counter +
      horizontalInterval
    min =
      tileMin +
      window.document.body.clientWidth / 2 +
      cloudTextAreaWidth / 2 +
      (tileDimensionsWidth * randomScaleValue) / 2 +
      horizontalInterval * counter
  } else if (isTop) {
    max =
      tileMin + horizontalInterval + horizontalInterval * counter - Math.abs(tileWidth * randomScaleValue - tileWidth)
    min = tileMin + horizontalInterval * counter - Math.abs(tileWidth * randomScaleValue - tileWidth)
  } else {
    max =
      tileMin +
      horizontalInterval +
      horizontalInterval * counter -
      Math.abs(tileWidth * randomScaleValue - tileWidth) -
      window.document.body.clientWidth +
      tileWidth
    min =
      tileMin +
      horizontalInterval * counter -
      Math.abs(tileWidth * randomScaleValue - tileWidth) -
      window.document.body.clientWidth
  }

  return getRandomNumber(min, max)
}

const getTilePosition = (idx: number, gridLocation: GridLocation[], xPositionTile: TilePositions): number => {
  if (gridLocation.length > 0 && idx < gridLocation.length) {
    const rowIndex = gridLocation[idx].rowIndex
    const isEven = gridLocation[idx].isEven
    if (isEven) {
      return xPositionTile.even[rowIndex]
    } else {
      return xPositionTile.odd[rowIndex]
    }
  }
  return 0
}

const getRowPosition = (
  idx: number,
  gridLocation: GridLocation[] = [],
  tileDimensionsHeight: number,
  tileSpacing: number
) => {
  if (gridLocation.length > 0 && idx < gridLocation.length) {
    return gridLocation[idx].row * (tileDimensionsHeight + tileSpacing)
  }
  return 0
}

const getFilledGridLocation = (numTiles: number, imageLength: number): GridLocation[] => {
  let startIndex = 0
  let isEven = true
  let counter = 0
  let rowCount = 0
  const gridLocation: GridLocation[] = []

  for (let i = 0; i < imageLength; i++) {
    if (isEven) {
      gridLocation.push({ rowIndex: i - startIndex, row: rowCount, isEven: true })
      if (counter == numTiles - 1) {
        isEven = false
        startIndex = startIndex + counter + 1
        counter = 0
        rowCount++
        continue
      }
      counter++
    } else if (!isEven) {
      gridLocation.push({ rowIndex: i - startIndex, row: rowCount, isEven: false })
      if (counter == numTiles) {
        isEven = true
        startIndex = startIndex + counter + 1
        counter = 0
        rowCount++
        continue
      }
      counter++
    }
  }

  return gridLocation
}

const getXPositionTileArray = (
  maxTiles: number,
  screenWidth: number,
  tileDimensionsWidth: number,
  tileSpacing: number
): TilePositions => {
  const xPositionTile: TilePositions = {
    even: [],
    odd: [],
  }

  // set even array
  const evenLeftPos = screenWidth / 2 - tileDimensionsWidth - tileSpacing / 2
  const evenRightPos = screenWidth / 2 + tileSpacing / 2
  xPositionTile.even.push(evenLeftPos, evenRightPos)

  for (let idx = 2; idx < maxTiles - 1; idx++) {
    if (idx % 2) {
      xPositionTile.even.push(evenRightPos + ((idx - 1) / 2) * (tileDimensionsWidth + tileSpacing))
    } else {
      xPositionTile.even.push(
        screenWidth / 2 - tileSpacing / 2 - ((idx + 2) / 2) * tileDimensionsWidth - (idx / 2) * tileSpacing
      )
    }
  }

  // set odd array
  const evenMiddlePos = screenWidth / 2 - tileDimensionsWidth / 2
  xPositionTile.odd.push(evenMiddlePos)

  for (let idx = 1; idx < maxTiles; idx++) {
    if (idx % 2) {
      xPositionTile.odd.push(evenMiddlePos + ((idx + 1) / 2) * (tileDimensionsWidth + tileSpacing))
    } else {
      xPositionTile.odd.push(evenMiddlePos - (idx / 2) * (tileDimensionsWidth + tileSpacing))
    }
  }

  return xPositionTile
}
const getContainerHeight = (
  gridLocation: { row: number }[],
  containerHeight: number,
  tileSpacing: number,
  tileDimensions: { height: number; width: number },
  aspectRatio: number
) => {
  if (gridLocation.length > 0) {
    containerHeight =
      (gridLocation[gridLocation.length - 1].row + 1) * (tileDimensions.height + tileSpacing) -
      tileSpacing +
      (-aspectRatio * tileDimensions.width) / 2

    const minAllowedContainerHeight = window.innerHeight + tileDimensions.height

    if (containerHeight < minAllowedContainerHeight) {
      containerHeight = minAllowedContainerHeight
    }
  }

  return containerHeight
}

const fillImageArrayWithRepeatImages = (
  tileDimensionsHeight: number,
  tileSpacing: number,
  maxTilesPerRow: number,
  cloudTiles: string[] = [],
  resizeTileAddTracker: number
): [number, string[]] => {
  const getRandomValuesFromArray = (arr: string[], count: number) => {
    return count <= arr.length ? [...arr].sort(() => Math.random() - 0.5).slice(0, count) : []
  }
  /* formula to derive num of rows of tiles to fill viewport is...
      Viewport Height = (tileHeight+tileSpacing) * numRows - (tileHeight/2) - padding
    */
  const numRows = Math.ceil(
    (innerHeight + tileDimensionsHeight / 2 + tileSpacing) / (tileDimensionsHeight + tileSpacing)
  )

  const numTilesToFillViewPort =
    Math.ceil(numRows / 2) * maxTilesPerRow + Math.floor(numRows / 2) * (maxTilesPerRow - 1)
  if (cloudTiles.length < numTilesToFillViewPort) {
    const newTiles = getRandomValuesFromArray(cloudTiles, numTilesToFillViewPort - cloudTiles.length)
    cloudTiles.push(...newTiles)

    resizeTileAddTracker += 1
  }

  return [resizeTileAddTracker, cloudTiles]
}

type dimension = {
  width: number
  height: number
}

// calculates the image tiles min and max x & y values positioned at the edges of the viewport
const getTileRanges = (
  imageLength,
  gridLocation: GridLocation[] = [],
  xPositionTile,
  containerRefWidth,
  tileDimensions: dimension,
  containerHeight,
  tileSpacing: number
): number[][] => {
  const tileRanges = []

  for (let idx = 0; idx < imageLength; idx++) {
    const rowIndex = gridLocation[idx].rowIndex
    const row = gridLocation[idx].row

    let xMin = 0
    let xMax = 0
    if (idx % 2) {
      xMin = -xPositionTile.odd[rowIndex]
      xMax = containerRefWidth - xPositionTile.odd[rowIndex]
    } else {
      xMin = -xPositionTile.even[rowIndex] - tileDimensions.width / 2
      xMax = containerRefWidth - xPositionTile.even[rowIndex] - tileDimensions.width / 2
    }

    if (row % 2) {
      xMin -= tileDimensions.width / 2
      xMax -= tileDimensions.width / 2
    }

    const yMin =
      Math.abs(containerHeight - window.innerHeight) / 2 -
      getRowPosition(idx, gridLocation, tileDimensions.height, tileSpacing) -
      tileDimensions.height / 2
    const yMax = yMin + window.innerHeight

    tileRanges.push([xMin, xMax, yMin, yMax])
  }

  return tileRanges
}

export {
  getBlurRange,
  durstenfeldArrayShuffle,
  getNumberOfTilesPerHalfHorizontally,
  getRandomNumber,
  getRandomConstrainedYCoord,
  getRandomConstrainedXCoord,
  getTilePosition,
  getRowPosition,
  getFilledGridLocation,
  getXPositionTileArray,
  getContainerHeight,
  fillImageArrayWithRepeatImages,
  getTileRanges,
}
